import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  Pagination,
  StatusBadge,
  Button,
  type Column,
  type StatusType,
  type ToggleOption,
} from '@themes/aegix';
import {
  useEventStaff,
} from '../../../hooks/queries/useReunification';
import { useFeature } from '../../../hooks/useFeature';
import { FeatureCode } from '../../../types/features';
import type { EventStaffAssignment } from '../../../types/ReunificationTypes';
import { StaffDetailDrawer } from './StaffDetailDrawer';
import { AddEventStaffDialog } from '../../reunification/AddEventStaffDialog';

const toggleOptions: ToggleOption[] = [
  { value: 'all', label: 'All' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'active', label: 'Active' },
];

interface StaffRow {
  [key: string]: unknown;
  id: string;
  name: string;
  role: string;
  station: string;
  status: StatusType;
  status_label: string;
  checked_in: string;
  raw_status: string;
  /** Enum value of `role` (the display string lives in `role`). */
  raw_role: string;
  /** Raw notes string — empty string when none, never null. */
  raw_notes: string;
  /** True when a station is currently assigned (drives the Clear button). */
  has_station: boolean;
}

function mapStaffStatus(status: string): StatusType {
  const statusMap: Record<string, StatusType> = {
    assigned: 'pending',
    checked_in: 'pending',
    active: 'active',
    released: 'inactive',
  };
  return statusMap[status] ?? 'pending';
}

const columns: Column<StaffRow>[] = [
  { key: 'name', label: 'Name', sortable: true, width: '180px' },
  { key: 'role', label: 'Role', sortable: true, width: '140px' },
  { key: 'station', label: 'Station', sortable: false, width: '140px' },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    width: '110px',
    render: (row) => <StatusBadge status={row.status} label={row.status_label} />,
  },
  { key: 'checked_in', label: 'Checked In', sortable: true, width: '140px' },
];

function transformStaff(staff: EventStaffAssignment[]): StaffRow[] {
  return staff.map((s) => ({
    id: s.public_id,
    name: s.staff_name,
    role: s.role_display,
    station: s.station_name ?? '---',
    status: mapStaffStatus(s.status),
    status_label: s.status_display,
    checked_in: s.checked_in_at
      ? new Date(s.checked_in_at).toLocaleString()
      : '---',
    raw_status: s.status,
    raw_role: s.role,
    raw_notes: s.notes ?? '',
    has_station: s.station_assignment !== null,
  }));
}

function collectExistingStaffUserIds(
  staff: EventStaffAssignment[] | undefined,
): number[] {
  if (!staff) return [];
  const ids = staff
    .map((s) => s.staff_member)
    .filter((id): id is number => Number.isFinite(id));
  return Array.from(new Set(ids));
}

interface EventStaffPanelProps {
  eventId: string;
}

import { useTranslation } from 'react-i18next';

export function EventStaffPanel({ eventId }: EventStaffPanelProps) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<StaffRow>();
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Surface the Add button only when the reunification feature is
  // enabled for this site. The backend's `CanManageStaffAssignments`
  // permission still gates the API and 403s any direct call regardless of
  // this flag. A dedicated `REUNIFICATION_MANAGE_STAFF` feature is tracked
  // as a follow-up to give districts finer-grained control without
  // disabling the whole reunification module.
  const canManageStaff = useFeature(FeatureCode.REUNIFICATION);

  const { data, isLoading, isFetching, error } = useEventStaff(eventId, {
    search: searchValue || undefined,
    status: statusFilter === 'all' ? undefined : (statusFilter as 'assigned' | 'active'),
    page: currentPage,
    page_size: pageSize,
    ordering: sortColumn
      ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`
      : undefined,
  });

  const staff = data?.results ? transformStaff(data.results) : [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const existingStaffUserIds = useMemo(
    () => collectExistingStaffUserIds(data?.results),
    [data?.results],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter, pageSize]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleRowClick = useCallback((row: StaffRow) => {
    setSelectedStaff(row);
  }, []);

  return (
    <>
      <Flex align="center" gap={3} pb={2}>
        <Box flex={1}>
          <TableOptions
            toggleOptions={toggleOptions}
            activeToggle={statusFilter}
            onToggleChange={(v: string) => {
              setStatusFilter(v);
              setCurrentPage(1);
            }}
            searchValue={searchValue}
            onSearchChange={(v) => {
              setSearchValue(v);
              setCurrentPage(1);
            }}
          />
        </Box>
        {canManageStaff && (
          <Button
            visual="primary"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            data-e2e="event-staff-add-button"
          >
            Add staff
          </Button>
        )}
      </Flex>

      <Box pb={3}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error ? error.message : 'Failed to load staff'}
            </Text>
          </Center>
        ) : staff.length === 0 ? (
          <Center py={12}>
            {isFetching ? (
              <Spinner size="lg" color="button.primary" />
            ) : (
              <Text color="text.secondary">{t('events.noStaffAssigned', 'No staff assigned')}</Text>
            )}
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={staff}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </Box>

      <StaffDetailDrawer
        isOpen={!!selectedStaff}
        onClose={() => setSelectedStaff(undefined)}
        eventId={eventId}
        staff={selectedStaff}
      />

      <AddEventStaffDialog
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        eventId={eventId}
        existingStaffUserIds={existingStaffUserIds}
      />
    </>
  );
}
